import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { PdBaseUIInput } from "../base/pd-base-ui-input.js";
import "../pd-range.js";
import { css, html, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
//#region src/pd-color-picker/PdColorPicker.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/** Convert 6-digit hex (#rrggbb) to { r, g, b } (0–255). */
function hexToRgb(hex) {
	const h = hex.replace(/^#/, "");
	return {
		r: parseInt(h.substring(0, 2), 16),
		g: parseInt(h.substring(2, 4), 16),
		b: parseInt(h.substring(4, 6), 16)
	};
}
/**
* Color picker input component.
*
* Renders a native `<input type="color">` with a hex text input and color preview swatch.
* Optionally shows an opacity slider (PdRange) in the same row.
*
* @tagname pd-color-picker
* @summary Color picker with hex input, swatch preview, and optional opacity slider.
*
* @event pd-form-element-change - Fired when color value changes. Detail: `{ value, name, parsedValue, state }`.
*/
var PdColorPicker = class extends PdBaseUIInput {
	constructor(..._args) {
		super(..._args);
		this.placeholder = "000000";
		this.opacity = false;
		this.initAlpha = 1;
		this._alpha = 1;
	}
	static {
		this.styles = [PdBaseUIInput.styles, css`
      :host {
        display: block;
      }

      .color-container {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        width: var(--pd-input-field-width, 250px);
        max-width: 100%;
      }

      .color-swatch {
        position: relative;
        width: 42px;
        height: 42px;
        min-width: 42px;
        border-radius: var(--pd-radius-md, 6px);
        border: 2px solid var(--pd-default-light-col, #cbd5e1);
        overflow: hidden;
        cursor: pointer;
        /* checkerboard base — visible through transparent colors */
        background: repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 0 0 /
          12px 12px;
      }

      .color-swatch-fill {
        position: absolute;
        inset: 0;
      }

      .color-swatch input[type="color"] {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        border: none;
        padding: 0;
        cursor: pointer;
      }

      .hex-field {
        flex: 1;
        display: flex;
        align-items: center;
        height: var(--pd-input-field-height, 2.5rem);
        min-width: 0;
        background: var(
          --pd-input-field-bg-col,
          var(--pd-default-bg-col, #fff)
        );
        border: var(
          --pd-input-field-border,
          1px solid var(--pd-default-light-col, #cbd5e1)
        );
        border-bottom: var(
          --pd-input-field-border-bottom,
          2px solid var(--pd-default-col, #2563eb)
        );
        border-radius: var(--pd-radius-md, 6px);
        box-sizing: border-box;
        overflow: hidden;
      }

      .hex-field:focus-within {
        border: var(
          --pd-input-field-border-focus,
          2px solid var(--pd-default-col, #2563eb)
        );
      }

      .hex-field.disabled {
        background: var(--pd-input-field-bg-col-disabled, #e9e9e9);
        border-bottom: var(
          --pd-input-field-border-bottom-disabled,
          2px solid var(--pd-default-disabled-col, #94a3b8)
        );
        cursor: not-allowed;
      }

      .hex-prefix {
        padding-left: 0.5rem;
        font-family: var(--pd-default-font-input-family, monospace);
        font-size: var(--pd-default-font-input-size, 0.95rem);
        color: var(--pd-default-font-muted-col, #94a3b8);
        user-select: none;
        line-height: 1;
      }

      .hex-input {
        flex: 1;
        height: 100%;
        padding: var(--pd-input-field-padding, 0.25rem) 0.5rem
          var(--pd-input-field-padding, 0.25rem) 0.25rem;
        font-family: var(--pd-default-font-input-family, monospace);
        font-size: var(--pd-default-font-input-size, 0.95rem);
        color: var(--pd-default-font-input-col, #1e293b);
        background: transparent;
        border: none;
        box-sizing: border-box;
        text-transform: uppercase;
        outline: none;
        min-width: 0;
      }

      .hex-input:disabled {
        cursor: not-allowed;
      }

      .color-container.error .hex-field {
        border: 1px solid var(--pd-default-error-col, #ef4444);
        background-color: var(--pd-default-error-light-col);
      }

      .color-container.focus.error .hex-field {
        border: 2px solid var(--pd-default-error-col, #ef4444);
      }

      .color-container.valid .hex-field {
        border: 1px solid var(--pd-default-success-col, #22c55e);
        background-color: var(--pd-default-success-light-col);
      }

      .color-swatch.empty {
        background:
          linear-gradient(
            to top right,
            transparent calc(50% - 1px),
            var(--pd-default-disabled-col, #878787) calc(50% - 1px),
            var(--pd-default-disabled-col, #878787) calc(50% + 1px),
            transparent calc(50% + 1px)
          ),
          linear-gradient(
            to top left,
            transparent calc(50% - 1px),
            var(--pd-default-disabled-col, #878787) calc(50% - 1px),
            var(--pd-default-disabled-col, #878787) calc(50% + 1px),
            transparent calc(50% + 1px)
          ),
          var(--pd-default-bg-col, #fff);
      }

      .color-swatch.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .color-swatch.disabled input[type="color"] {
        pointer-events: none;
      }

      /* ── Opacity range (inline) ─────────────────────────────────── */

      .opacity-range {
        flex: 0 1 120px;
        min-width: 80px;
        --pd-input-field-width: 100%;
        --pd-input-field-height: var(--pd-input-field-height, 2.5rem);
      }

      .opacity-value {
        flex: 0 0 4ch;
        font-family: var(--pd-default-font-input-family, monospace);
        font-size: var(--pd-default-font-input-size, 0.95rem);
        color: var(--pd-default-font-muted-col, #94a3b8);
        text-align: right;
        user-select: none;
        line-height: 1;
      }
    `];
	}
	willUpdate(changedProps) {
		if (changedProps.has("initAlpha") && this._phase === "init") this._alpha = this.initAlpha;
	}
	render() {
		const inputId = `${this.id}ColorPicker`;
		const errorId = `${this.id}Error`;
		const isEmpty = !this._value;
		const currentColor = this._value || "#000000";
		const displayValue = this._value ? this._value.replace(/^#/, "") : "";
		return html`
      ${this._renderLabel(inputId)}
      <div class="${classMap(this.getClassmap({ "color-container": true }))}">
        <div
          class="color-swatch ${this.disabled ? "disabled" : ""} ${isEmpty ? "empty" : ""}"
        >
          ${isEmpty ? nothing : html`<div
                  class="color-swatch-fill"
                  style="background:${this._rgbaString()}"
                ></div>`}
          <input
            type="color"
            .value="${currentColor}"
            ?disabled="${this.disabled}"
            @input="${this._onColorInput}"
            @change="${this._onColorChange}"
            aria-label="${this.label || "Farbauswahl"}"
          />
        </div>
        <div class="hex-field ${this.disabled ? "disabled" : ""}">
          <span class="hex-prefix">#</span>
          <input
            id="${inputId}"
            type="text"
            class="hex-input"
            .value="${displayValue}"
            placeholder="${this.placeholder}"
            ?disabled="${this.disabled}"
            ?readonly="${this.readonly}"
            maxlength="6"
            @input="${this._onHexInput}"
            @blur="${this._onHexBlur}"
            aria-describedby="${errorId}"
          />
        </div>
        ${this.opacity ? this._renderOpacityRange() : nothing}
      </div>
      ${this._renderErrorMsg(errorId)}
    `;
	}
	_renderOpacityRange() {
		const pct = Math.round(this._alpha * 100);
		return html`
      <pd-range
        class="opacity-range"
        min="0"
        max="100"
        step="1"
        no-ticks
        no-label
        .initValue="${String(pct)}"
        ?disabled="${this.disabled}"
        internalElement
        @pd-form-element-change="${this._onOpacityChange}"
      ></pd-range>
      <span class="opacity-value">${pct}%</span>
    `;
	}
	_onOpacityChange(e) {
		e.stopPropagation();
		const detail = e.detail;
		const pct = Number(detail.value);
		this._alpha = Math.round(pct) / 100;
		this._dispatchChange(this._value);
	}
	/** CSS rgba() string for the current color + alpha. */
	_rgbaString() {
		const { r, g, b } = hexToRgb(this._value || "#000000");
		return `rgba(${r},${g},${b},${this._alpha})`;
	}
	_onColorInput(event) {
		const input = event.target;
		this._handleChangedValue(input.value, event, true);
	}
	_onColorChange(event) {
		const input = event.target;
		this._handleChangedValue(input.value, event, true);
	}
	_onHexInput(event) {
		const raw = event.target.value.replace(/#/g, "").trim();
		if (raw === "") {
			this._handleChangedValue("", event, true);
			return;
		}
		if (/^[0-9a-fA-F]{6}$/.test(raw)) this._handleChangedValue("#" + raw.toLowerCase(), event, true);
	}
	_onHexBlur(event) {
		const raw = event.target.value.replace(/#/g, "").trim();
		if (raw === "") {
			this._handleChangedValue("", event, true);
			return;
		}
		if (/^[0-9a-fA-F]{6}$/.test(raw)) this._handleChangedValue("#" + raw.toLowerCase(), event, true);
		else if (/^[0-9a-fA-F]{3}$/.test(raw)) {
			const expanded = "#" + raw[0] + raw[0] + raw[1] + raw[1] + raw[2] + raw[2];
			this._handleChangedValue(expanded.toLowerCase(), event, true);
		}
	}
	_getParsedValue() {
		const hex = this._value || "#000000";
		const { r, g, b } = hexToRgb(hex);
		const a = this.opacity ? this._alpha : 1;
		return {
			hex,
			r,
			g,
			b,
			a,
			rgb: `rgb(${r},${g},${b})`,
			rgba: `rgba(${r},${g},${b},${a})`
		};
	}
};
__decorate([property({ type: String })], PdColorPicker.prototype, "placeholder", void 0);
__decorate([property({ type: Boolean })], PdColorPicker.prototype, "opacity", void 0);
__decorate([property({
	type: Number,
	attribute: "init-alpha"
})], PdColorPicker.prototype, "initAlpha", void 0);
__decorate([query("input[type=\"color\"]")], PdColorPicker.prototype, "_colorInput", void 0);
__decorate([query("input[type=\"text\"]")], PdColorPicker.prototype, "_textInput", void 0);
__decorate([state()], PdColorPicker.prototype, "_alpha", void 0);
//#endregion
export { PdColorPicker };
